import { INLINES } from '@contentful/rich-text-types';
import { appendUrlParam } from 'connectors/utils/append-url-param';
import { generateLink } from 'connectors/utils/generate-link';
import { getLegacyLink } from 'connectors/utils/get-legacy-link';
import { loadRichTextLinks } from 'connectors/utils/load-rich-text-links';
import { resolveRichTextLinks } from 'connectors/utils/resolve-rich-text-links';
import { getEntryId, isEntryHyperlink } from 'connectors/utils/resolve-rich-text-node';
import { Region } from 'constants/shop';
import isObject from 'lodash/isObject';
import { PageReference } from 'types/page-reference';

interface ResolveReferences<T> {
  node: T;
  region: Region;
  locale?: string;
  links?: Partial<Record<string, string>>;
}

/**
 * Resolves links in a Contentful response in place and returns the processed node.
 * Referenced pages are removed from the payload; shared objects are processed once.
 */
export const resolveReferences = async <T>({
  node: input,
  region,
  locale,
  links: preparedLinks,
}: ResolveReferences<T>): Promise<T> => {
  const links = preparedLinks ?? (await loadRichTextLinks({ node: input, region, locale }));
  const seen = new WeakSet<object>();
  return resolveNode(input);

  function resolveNode<N>(node: N): N {
    if (!isObject(node) || seen.has(node)) {
      return node;
    }

    seen.add(node);

    if (isEntryHyperlink(node)) {
      const entryId = getEntryId(node.data.target);
      const uri = links[entryId ?? ''];

      if (!uri) {
        console.error('[contentful] Rich-text hyperlink without a resolved URL', entryId);
      }

      Object.assign(node, { nodeType: INLINES.HYPERLINK, data: { uri: uri || '#' } });
      return node;
    }

    if ('buttonLink' in node) {
      node.buttonLink = resolveRichTextLinks({ value: node.buttonLink, links });
    }

    if ('reference' in node || 'urlParam' in node) {
      // Contentful defines this field's entry type; the generic traversal loses that schema.
      const { reference, urlParam, ...fields } = node as Record<string, unknown> & {
        reference?: PageReference;
      };
      // reference wins; without it the legacy text link is the base and keeps working
      const base = generateLink(reference) || getLegacyLink(fields);
      const link = appendUrlParam({ base, urlParam });

      if (!link && reference) {
        console.warn('[contentful] reference without a handle', reference.sys?.id);
      }

      // Rest loses N; preserve the existing CMS type, omitting only consumed link fields.
      return resolveNode({
        ...fields,
        ...(link ? { link } : {}),
      } as typeof node);
    }

    for (const [key, value] of Object.entries(node)) {
      // Contentful freezes system metadata; it contains no content links to resolve.
      if (key === 'sys') {
        continue;
      }

      node[key] = resolveNode(value);
    }

    return node;
  }
};
